import './Select.scss';
import Control from "../../Control";
import Delegate from "../../Delegate";
import NP from "../../NP";
import Font from "../../Font";


export interface ISelectItem {
	Value?: any;
	Id?: string;
	IsSeparator?: boolean
	FontFamily?: string;
	Content?: string;
	RK?: string;
	Font?: Font;
	Data?: any;
	Tag?: any;
}

export interface ISelectItemArgs {
	Item: ISelectItem;
}


const NULL = "__null__";

export default class Select extends Control {

	public EvItemSelected: Delegate;
	public EvBlur: Delegate;
	public EvFocus: Delegate;

	protected _Items: Array<ISelectItem>;
	protected _FloatingLabel: string;
	protected _CustomInput: Control;
	protected _SelectedIndex: number;
	protected _SelectedItem: ISelectItem;
	private _EmptyLabel: boolean;
	private _FullWidth: boolean;
	private _IsOutlined: boolean;

	protected _selectInput: HTMLSelectElement;
	protected _selectInputOutline: HTMLSelectElement;
	protected _selectInputContainer: HTMLSelectElement;
	protected _selectIcon: HTMLSelectElement;

	constructor(settings?: object)
	{
		super(settings);
		this._render();
	}

	_initFields(settings: object)
	{
		this._Items = [];
		this._SelectedItem = null;
		this._SelectedIndex = -1;
		this._FloatingLabel = "";
		this._EmptyLabel = false;
		this._FullWidth = false;
		this._IsOutlined = true;
		this.EvItemSelected = new Delegate();
		this.EvBlur = new Delegate();
		this.EvFocus = new Delegate();
		super._initFields(settings);
	}

	_render()
	{
		let label = this._FloatingLabel || this._EmptyLabel ? `<label class="select__label">${this._FloatingLabel}</label>` : '';

		this._initComponent(
			 `<div class="select">
				 ${label}
				 <div class="select__native-control-container">
					  <div class="select__outline"></div>
					  <i class="select__icon app-list-item__graphic ${NP.FontImages.spin_down}"></i>
				 </div>
			</div>`);

		this._renderItems([this._root], this._ParentNode || document.documentElement);

		this._selectInputContainer = this._root.querySelector('.select__native-control-container');
		this._selectInputOutline = this._root.querySelector('.select__outline');
		this._selectIcon = this._root.querySelector('.select__icon');

		if (this._CustomInput)
		{
			this._CustomInput.putToNode(this._selectInputContainer, this._selectInputOutline);
			this._selectInput = this._CustomInput.getRoot() as HTMLSelectElement;
		}
		else
		{
			this._selectInput = NP.createNode(this._selectInputContainer, 'select__native-control', 'select', this._selectInputOutline);
			this.setItems(this._Items, false);

			if (this._SelectedIndex > -1)
				this.setSelectedIndex(this._SelectedIndex, false);
			else
				this.setValue(this._Value, false);
			this._selectInput.addEventListener('change', () =>
			{
				this.setSelectedItem(this._Items[this._selectInput.selectedIndex]);
			});
			this._selectInput.addEventListener('blur', () =>
			{
				this.setActive(false);
				this.EvBlur.fire(this)
			});
			this._selectInput.addEventListener('focus', () =>
			{
				this.setActive(true);
				this.EvFocus.fire(this)
			});
		}

		if (this._FullWidth)
			this._root.style.width = "100%";
	}

	setActive(active: boolean)
	{
		if (active)
		{
			NP.addCssClass(this._selectInputContainer, 'select__native-control-container--active mdc-select--focused');
			NP.addCssClass(this._selectIcon, 'select__icon--active');
		}
		else
		{
			NP.removeCssClass(this._selectInputContainer, 'select__native-control-container--active mdc-select--focused');
			NP.removeCssClass(this._selectIcon, 'select__icon--active');
		}
	}

	setItemVisible(itemValue: any, visible: boolean)
	{
		let node = this._root.querySelector(`option[value="${itemValue}"]`);
		if (visible)
			NP.showNode(node);
		else
			NP.hideNode(node);
	}

	addItems(items: Array<ISelectItem>)
	{
		if (items)
			items.forEach(item => this.addItem(item));
	}

	addItem(value: ISelectItem)
	{
		if (value)
		{
			let option = NP.createNode(this._selectInput, '', 'option');
			if (!value.IsSeparator)
			{
				option.setAttribute('value', value.Value === null ? NULL : value.Value);
				option.innerText = value.Content || NP.RM.getString(value.RK);
				if (value.FontFamily)
					option.style.fontFamily = value.FontFamily;

				if (value.Font)
					option.setAttribute("style", value.Font.toCSS());
			}
			else
				option.disabled = false;
			this._Items.push(value);
		}
	}

	getItems()
	{
		return this._Items;
	}

	getItem(value: any)
	{
		return this.getItems().find(x => x.Value == value);
	}

	getItemById(id: string)
	{
		return this.getItems().find(x => x.Id == id);
	}

	setEnabled(value: boolean)
	{
		super.setEnabled(value);
		if (this._selectInput)
		{
			if (this._Enabled)
				this._selectInput.removeAttribute('disabled');
			else
				this._selectInput.setAttribute('disabled', 'disabled');
		}
	}

	setItems(items: Array<ISelectItem>, _clear?: boolean)
	{
		if (_clear !== false)
			this.clear();
		this._Items = [];
		if (items)
			this.addItems(items);
		if (this._selectInput)
			this._selectInput.value = "";
	}

	getSelectedItem()
	{
		return this._SelectedItem;
	}

	getValue()
	{
		let selectedItem = this.getSelectedItem();
		return selectedItem ? selectedItem.Value : null;
	}

	clearSelection()
	{
		if (this._SelectedItem)
		{
			this._selectInput.value = null;
			this._SelectedItem = null;
		}
	}

	setValue(value: any, fireEvent?: boolean)
	{
		this.setSelectedItem(this.getItem(value), fireEvent);
	}

	setSelectedItem(item: ISelectItem, fireEvent?: boolean)
	{
		this.clearSelection();
		if (item)
		{
			this._selectInput.value = item.Value === null ? NULL : item.Value;
			this._SelectedItem = item;
			if (fireEvent !== false)
				this.EvItemSelected.fire(this, {Item: item});
		}
	}

	setSelectedIndex(index: number, fireEvent?: boolean)
	{
		this.setSelectedItem(this._Items[index], fireEvent);
	}

	clear()
	{
		this._selectInput.value = "";
		this._Items = [];
		this._selectInput.innerHTML = "";
	}

	getItemByPropValue(propName: string, value: any)
	{
		return this.getItems().find(item => (<any>item)[propName] == value);
	}
};